import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Rewards.module.css";
import HeaderBar from "../HeaderBar/HeaderBar";
import { getAppSumoReferrals } from "../../Store/apiStore";
import {
  InfoIcon,
  ClockIcon,
  PeopleIcon,
  ShareIcon,
  CheckIcon,
  LinkIcon,
  CopyIcon,
} from "./icons";

const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * "Referrals & Earnings" — the partner's link, everyone who signed up through
 * it, and what each is worth.
 *
 * The two headline numbers answer different questions and are deliberately not
 * the same: "Minutes Earned" is what next month's deposit will be if nothing
 * changes, while the balance on the hub screen is what is spendable today.
 * Referral credits arrive on the 1st, so a referral that paid yesterday shows
 * here immediately but is not spendable until then.
 */
const ReferralsEarnings = ({ mode = "referrals" }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Which list this screen is showing. Fixed by the route rather than by a tab:
  // the earnings list is reached from the balance card on the hub, and the
  // referral list from the Refer & Earn card, so each arrives already decided.
  const tab = mode;
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getAppSumoReferrals()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  // Built from the current origin so a link copied in dev points at dev. The
  // vanity name wins when the account has one — it is the friendlier share.
  const link = useMemo(() => {
    const slug = data?.referralName || data?.referralCode;
    if (!slug) return "";
    return `${window.location.origin}?referral=${encodeURIComponent(slug)}`;
  }, [data]);

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Clipboard is blocked outside a secure context; select the field so the
      // user can copy by hand rather than leaving them with nothing.
      const el = document.getElementById("referral-link-input");
      el?.select?.();
      return;
    }
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1800);
  };

  const handleShare = async () => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Rexpt — AI Receptionist",
          text: "Never miss a business call again. Try Rexpt:",
          url: link,
        });
        return;
      } catch {
        /* dismissed — fall through to copy */
      }
    }
    handleCopy();
  };

  const referrals = data?.referrals || [];
  const paid = referrals.filter((r) => r.minutesPerMonth > 0);

  // The three states a referral can be in, from the two fields the API gives:
  // a plan means they bought, an active status means they are still paying.
  const bucketOf = (r) => {
    if (!r.planName) return "signed";
    return r.status === "active" ? "purchased" : "inactive";
  };

  const base = tab === "earnings" ? paid : referrals;
  const rows =
    filter === "all" ? base : base.filter((r) => bucketOf(r) === filter);

  const FILTERS = [
    { key: "all", label: "All" },
    { key: "signed", label: "Signed up" },
    { key: "purchased", label: "Purchased" },
    { key: "inactive", label: "Inactive" },
  ];

  return (
    <div className={styles.wrap}>
      <div className={styles.inner}>
        {/* Back arrow, title and the info button share one line — the strapline
            below it said nothing the stats underneath do not already show. */}
        <div className={styles.headerSlot}>
          <HeaderBar
            title="Referrals & Earnings"
            rightIcon={
              <button
                type="button"
                className={styles.infoDot}
                onClick={() => navigate("/rewards")}
                aria-label="About referrals"
              >
                <InfoIcon width={16} height={16} />
              </button>
            }
          />
        </div>

        {loading ? (
          <div className={styles.loading}>Loading…</div>
        ) : (
          <>
            <div className={styles.statRow}>
              <div className={styles.stat}>
                <div>
                  <span className={styles.statLabel}>Total Referrals</span>
                  <span className={styles.statValue}>
                    {data?.totals?.referrals ?? 0}
                  </span>
                </div>
                <span className={styles.statIcon}>
                  <PeopleIcon width={18} height={18} />
                </span>
              </div>

              <div className={`${styles.stat} ${styles.statAccent}`}>
                <div>
                  <span className={styles.statLabel}>Minutes Earned</span>
                  <span className={styles.statValue}>
                    {(data?.totals?.minutesPerMonth ?? 0).toLocaleString()}
                  </span>
                  <span className={styles.statUnit}>mins</span>
                </div>
                <span className={styles.statIcon}>
                  <ClockIcon width={18} height={18} />
                </span>
              </div>
            </div>

            {/* Same box as the rewards landing screen, so the link looks the
                same wherever it is copied from. Share is the icon beside the
                label rather than a second full-width button. */}
            <div className={styles.linkBox}>
              <div className={styles.linkHead}>
                <span className={styles.linkLabel}>~Your referral link</span>
                <button
                  type="button"
                  className={styles.linkShare}
                  onClick={handleShare}
                  disabled={!link}
                  aria-label="Share referral link"
                >
                  <ShareIcon />
                </button>
              </div>
              <div className={styles.linkRow}>
                <span className={styles.linkIcon}>
                  <LinkIcon />
                </span>
                <input
                  id="referral-link-input"
                  className={styles.linkInput}
                  value={link}
                  readOnly
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className={styles.linkCopy}
                  onClick={handleCopy}
                  disabled={!link}
                >
                  <CopyIcon />
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            {/* Tabs, not local state: the two lists already live on their own
                routes (this screen and /rewards/earnings), so the tabs just
                navigate. Nothing about how either list loads changes. */}
            <div className={styles.tabRow}>
              <button
                type="button"
                className={`${styles.tab} ${
                  tab === "earnings" ? "" : styles.tabActive
                }`}
                onClick={() => navigate("/rewards/referrals")}
              >
                Referrals
              </button>
              <button
                type="button"
                className={`${styles.tab} ${
                  tab === "earnings" ? styles.tabActive : ""
                }`}
                onClick={() => navigate("/rewards/earnings")}
              >
                Earnings
              </button>
            </div>

            <div className={styles.chipRow}>
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`${styles.chip} ${
                    filter === f.key ? styles.chipActive : ""
                  }`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {rows.length === 0 ? (
              <div className={styles.rowList}>
                <div className={styles.empty}>
                  <div className={styles.emptyIcon}>
                    <PeopleIcon width={32} height={32} />
                  </div>
                  {tab === "earnings"
                    ? "No earnings yet. You earn minutes when someone you referred subscribes to a paid plan."
                    : "No referrals yet. Share your link above to get started."}
                </div>
              </div>
            ) : (
              <div className={styles.refList}>
                {rows.map((r) => (
                  <div className={styles.refRow} key={r.userId}>
                    <div className={styles.rowMain}>
                      <span className={styles.rowName}>
                        {r.name || r.email || "Rexpt user"}
                      </span>
                      <span className={styles.rowSub}>{r.email || ""}</span>
                    </div>
                    <div className={styles.rowRight}>
                      {/* A bought plan is a tinted pill; a bare sign-up is
                          plain text, so the eye finds the payers first. */}
                      {r.planName ? (
                        <span
                          className={`${styles.planPill} ${
                            /scaler/i.test(r.planName)
                              ? styles.planPillWarm
                              : styles.planPillCool
                          }`}
                        >
                          Purchase {r.planName}
                        </span>
                      ) : (
                        <span className={styles.rowPlan}>Signed up</span>
                      )}
                      <span
                        className={`${styles.rowMins} ${
                          r.minutesPerMonth ? "" : styles.rowMinsZero
                        }`}
                      >
                        {r.minutesPerMonth
                          ? `${r.minutesPerMonth.toLocaleString()} mins`
                          : "0 mins"}
                      </span>
                      <span className={styles.rowDate}>
                        {fmtDate(r.firstPaidAt || r.signedUpAt)}
                      </span>
                    </div>

                    {/* Paying vs merely signed up, as one glyph at the row's
                        edge: a green tick earns minutes, a clock is waiting. */}
                    <span
                      className={`${styles.refState} ${
                        r.minutesPerMonth
                          ? styles.refStateOn
                          : styles.refStateWait
                      }`}
                    >
                      {r.minutesPerMonth ? (
                        <CheckIcon width={13} height={13} />
                      ) : (
                        <ClockIcon width={20} height={20} />
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
};

export default ReferralsEarnings;
